import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * משמרת קבועה: תבנית על העובד, לא שיבוץ (מיגרציה 052).
 *
 * "נועה עובדת כל שני בוקר בטאגור". היא נכנסת לתוקף כשנפתח שבוע
 * חדש, ולכן השבוע מגיע כמעט בנוי.
 */
export type RecurringShift = {
  id: string;
  employee_id: string;
  branch_id: string;
  weekday: number;
  shift_type_id: string;
  start_time: string | null;
  end_time: string | null;
};

const KEY = ['recurring-shifts'] as const;

/** התבניות של עובד אחד. נטענות רק כשפותחים את המשמרת שלו. */
export function useRecurringFor(employeeId: string | null) {
  return useQuery({
    queryKey: [...KEY, employeeId ?? 'none'] as const,
    enabled: employeeId !== null,
    queryFn: async (): Promise<RecurringShift[]> => {
      const { data, error } = await supabase
        .from('recurring_shifts')
        .select('id, employee_id, branch_id, weekday, shift_type_id, start_time, end_time')
        .eq('employee_id', employeeId ?? '')
        .eq('is_active', true)
        .order('weekday');
      if (error) throw error;
      return (data ?? []) as RecurringShift[];
    },
  });
}

export function useRecurringMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: KEY });
    // התבנית משנה שבועות עתידיים, ולכן גם הלוח
    void queryClient.invalidateQueries({ queryKey: ['schedule-week'] });
    void queryClient.invalidateQueries({ queryKey: ['schedule-hours'] });
  };

  const create = useMutation({
    mutationFn: async (input: {
      employee_id: string;
      branch_id: string;
      weekday: number;
      shift_type_id: string;
      start_time: string | null;
      end_time: string | null;
      starts_on: string;
    }) => {
      const { error } = await supabase.from('recurring_shifts').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('recurring_shifts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** מילוי השבוע המוצג מהתבניות, לשבועות שנפתחו לפני שנוצרו. */
  const applyWeek = useMutation({
    mutationFn: async (input: { week_start: string; branch_ids: string[] }) => {
      const { data, error } = await supabase.rpc('apply_recurring_shifts', {
        p_week_start: input.week_start,
        p_branches: input.branch_ids.length > 0 ? input.branch_ids : undefined,
      });
      if (error) throw error;
      return (data as unknown as number) ?? 0;
    },
    onSuccess: refresh,
  });

  /** כמה ייווצרו, בלי ליצור. אותה שאילתה שעושה את העבודה. */
  const previewWeek = useMutation({
    mutationFn: async (input: { week_start: string; branch_ids: string[] }) => {
      const { data, error } = await supabase.rpc('recurring_week_preview', {
        p_week_start: input.week_start,
        p_branches: input.branch_ids.length > 0 ? input.branch_ids : undefined,
      });
      if (error) throw error;
      return (data ?? { total: 0, branches: [] }) as unknown as {
        total: number;
        branches: { branch_id: string; name: string; copied: number }[];
      };
    },
  });

  return { create, remove, applyWeek, previewWeek };
}
